import { MONTH_NAMES } from "@/constants";
import type { PeriodWidth } from "@/forwards2/periodUtils";
import {
  selectedWidth,
  type MandateEditorForm,
} from "@/forwards2/mandates/mandateEditorForm";

/**
 * Whether the volumes editor shows its simple form — the four quarters of a recurring listing and
 * nothing else to choose — rather than every listing shape.
 *
 * Staff choose it per mandate (`advanced_listing`), and the choice holds for staff and producers alike
 * (user decision, 2026-10-12). A listing the simple form cannot show still gets every shape, so a
 * mandate saved through the API with the box off never hides what it lists.
 */
export function usesSimpleListing(form: MandateEditorForm): boolean {
  return !form.advanced_listing && hasSimpleShape(form);
}

/**
 * Whether the simple form can show the listing: recurring, quarterly, and selling next year's
 * production only. Selling ahead is an advanced option, so an early share holds the advanced box
 * ticked like any other shape the simple form cannot show. The production range shows in the
 * simple form too, so it has no say here.
 */
export function hasSimpleShape(form: MandateEditorForm): boolean {
  return (
    form.listing_kind === "recurring" &&
    form.sales_plan.early_share_pct <= 0 &&
    selectedWidth(form) === "quarter"
  );
}

/**
 * A recurring template's slot as the editor and the summary name it: `Q2`, `H1`, the month's name,
 * or the translated "whole year" for a yearly template's one slot.
 */
export function recurringSlotLabel(
  width: PeriodWidth,
  slot: number,
  t: (key: string) => string
): string {
  if (width === "month") return MONTH_NAMES[slot - 1] ?? String(slot);
  if (width === "quarter") return `Q${slot}`;
  if (width === "half_year") return `H${slot}`;
  return t("forwards.mandates.volumes.whole_year");
}
